"use client";

import { EnhancedMemoryLeakDetector } from "@/utils/Memory/helpers/enhancedMemoryLeakDetector";
import { MemoryLeakFixer } from "@/utils/Memory/helpers/memoryLeakFixer";
import { PersistentMemoryTracker } from "@/utils/Memory/helpers/persistentMemoryTracker";
import { RefreshMemoryAnalyzer } from "@/utils/Memory/helpers/refreshMemoryAnalyzer";
import { useEffect, useState } from "react";
import { LuX } from "react-icons/lu";

interface ComprehensiveMemoryDiagnosticProps {
  onClose?: () => void;
}

export function ComprehensiveMemoryDiagnostic({
  onClose,
}: ComprehensiveMemoryDiagnosticProps) {
  const [isMonitoring, setIsMonitoring] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [report, setReport] = useState<any>(null);
  const [memoryData, setMemoryData] = useState<{
    used: number;
    total: number;
    limit: number;
  } | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [refreshAnalysis, setRefreshAnalysis] = useState<any>(null);

  useEffect(() => {
    if (isMonitoring) {
      const interval = setInterval(() => {
        const detector = EnhancedMemoryLeakDetector.getInstance();
        const analyzer = RefreshMemoryAnalyzer.getInstance();

        setReport(detector.getReport());
        setRefreshAnalysis(analyzer.getAnalysis());

        if (typeof window !== "undefined" && "memory" in performance) {
          const memory = (
            performance as {
              memory: {
                usedJSHeapSize: number;
                totalJSHeapSize: number;
                jsHeapSizeLimit: number;
              };
            }
          ).memory;
          setMemoryData({
            used: Math.round(memory.usedJSHeapSize / 1024 / 1024),
            total: Math.round(memory.totalJSHeapSize / 1024 / 1024),
            limit: Math.round(memory.jsHeapSizeLimit / 1024 / 1024),
          });
        }
      }, 2000);

      return () => clearInterval(interval);
    }
  }, [isMonitoring]);

  const handleForceGC = () => {
    const fixer = MemoryLeakFixer.getInstance();
    fixer.forceCleanup();
    console.log("🧹 Forced memory cleanup and garbage collection");
  };

  const handleClearData = () => {
    const detector = EnhancedMemoryLeakDetector.getInstance();
    const analyzer = RefreshMemoryAnalyzer.getInstance();
    const tracker = PersistentMemoryTracker.getInstance();

    detector.clearData();
    analyzer.clearData();
    tracker.clearData();

    setReport(null);
    setRefreshAnalysis(null);
  };

  if (process.env.NODE_ENV !== "development") {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 bg-gray-900 text-white p-4 rounded-lg text-sm z-50 max-w-2xl">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-lg text-white">Memory Diagnostic</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsMonitoring(!isMonitoring)}
            className={`px-4 py-2 rounded text-sm font-semibold ${
              isMonitoring
                ? "bg-red-600 hover:bg-red-700"
                : "bg-green-600 hover:bg-green-700"
            }`}
          >
            {isMonitoring ? "Stop Monitoring" : "Start Monitoring"}
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 rounded text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
              title="Close Memory Diagnostic"
            >
              <LuX className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Memory Status */}
      {memoryData && (
        <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-gray-800 rounded">
          <div>
            <div className="text-xs text-gray-400">Memory Usage</div>
            <div className="text-lg font-bold">
              {memoryData.used}MB / {memoryData.limit}MB
            </div>
            <div className="text-xs text-gray-400">
              {Math.round((memoryData.used / memoryData.limit) * 100)}% used
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-400">Total Memory</div>
            <div className="text-lg font-bold">{memoryData.total}MB</div>
            <div className="text-xs text-gray-400">JS Heap Size</div>
          </div>
        </div>
      )}

      {/* Refresh Analysis */}
      {refreshAnalysis && (
        <div className="mb-4 p-3 bg-gray-800 rounded">
          <h4 className="font-semibold text-sm mb-2">Refresh Analysis</h4>
          <div className="text-xs space-y-1">
            <div className="flex justify-between">
              <span>Memory Increase:</span>
              <span
                className={
                  refreshAnalysis.memoryIncrease > 20
                    ? "text-red-400 font-bold"
                    : "text-green-400"
                }
              >
                +{refreshAnalysis.memoryIncrease}MB
              </span>
            </div>
            <div className="flex justify-between">
              <span>DOM Nodes:</span>
              <span
                className={
                  refreshAnalysis.domNodeIncrease > 100
                    ? "text-red-400"
                    : "text-green-400"
                }
              >
                +{refreshAnalysis.domNodeIncrease}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Leak Severity:</span>
              <span
                className={
                  refreshAnalysis.leakSeverity === "critical"
                    ? "text-red-400 font-bold"
                    : refreshAnalysis.leakSeverity === "high"
                    ? "text-red-400"
                    : refreshAnalysis.leakSeverity === "medium"
                    ? "text-yellow-400"
                    : refreshAnalysis.leakSeverity === "low"
                    ? "text-yellow-400"
                    : "text-green-400"
                }
              >
                {refreshAnalysis.leakSeverity.toUpperCase()}
              </span>
            </div>
            {refreshAnalysis.suspectedCauses.length > 0 && (
              <div className="mt-2">
                <div className="text-yellow-400 font-semibold">
                  Suspected Causes:
                </div>
                {refreshAnalysis.suspectedCauses.map(
                  (cause: string, index: number) => (
                    <div key={index} className="ml-2 text-yellow-300">
                      • {cause}
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Memory Leak Report */}
      {report && report.leakReport && (
        <div className="mb-4 p-3 bg-gray-800 rounded">
          <h4 className="font-semibold text-sm mb-2">Memory Leak Report</h4>
          <div className="text-xs space-y-1">
            {report.leakReport.isLeakDetected ? (
              <div className="text-red-400 font-bold">
                🚨 LEAK DETECTED! Severity:{" "}
                {report.leakReport.leakSeverity.toUpperCase()}
              </div>
            ) : (
              <div className="text-green-400">✅ No leaks detected</div>
            )}
            {report.leakReport.memoryIncrease > 0 && (
              <div>Memory Increase: +{report.leakReport.memoryIncrease}MB</div>
            )}
            {report.leakReport.domNodeIncrease > 0 && (
              <div>DOM Nodes: +{report.leakReport.domNodeIncrease}</div>
            )}
            {report.leakReport.eventListenerIncrease > 0 && (
              <div>
                Event Listeners: +{report.leakReport.eventListenerIncrease}
              </div>
            )}
            {report.leakReport.timerIncrease > 0 && (
              <div>Active Timers: +{report.leakReport.timerIncrease}</div>
            )}
            {report.leakReport.recommendations.length > 0 && (
              <div className="mt-2">
                <div className="text-yellow-400 font-semibold">
                  Recommendations:
                </div>
                {report.leakReport.recommendations.map(
                  (rec: string, index: number) => (
                    <div key={index} className="ml-2 text-yellow-300">
                      • {rec}
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* System Resources */}
      {report && (
        <div className="mb-4 p-3 bg-gray-800 rounded">
          <h4 className="font-semibold text-sm mb-2">System Resources</h4>
          <div className="text-xs space-y-1">
            <div className="flex justify-between">
              <span>DOM Nodes:</span>
              <span>{report.domNodes || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Event Listeners:</span>
              <span>{report.eventListeners || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Active Timers:</span>
              <span>{report.activeTimers || 0}</span>
            </div>
          </div>
        </div>
      )}

      {/* Problematic Components */}
      {report &&
        report.componentRenders &&
        typeof report.componentRenders === "object" &&
        Object.keys(report.componentRenders).length > 0 && (
          <div className="mb-4 p-3 bg-gray-800 rounded">
            <h4 className="font-semibold text-sm mb-2">
              Problematic Components
            </h4>
            <div className="text-xs max-h-32 overflow-y-auto">
              {Object.entries(report.componentRenders)
                .filter(([, count]) => typeof count === "number")
                .sort(([, a], [, b]) => (b as number) - (a as number))
                .slice(0, 10)
                .map(([component, count]) => {
                  const renderCount = count as number;
                  return (
                    <div key={component} className="flex justify-between py-1">
                      <span className="truncate max-w-32">{component}:</span>
                      <span
                        className={
                          renderCount > 20
                            ? "text-red-400 font-bold"
                            : renderCount > 10
                            ? "text-yellow-400 font-semibold"
                            : "text-green-400"
                        }
                      >
                        {renderCount}
                      </span>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

      {/* Action Buttons */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={handleForceGC}
          className="px-3 py-2 bg-red-600 hover:bg-red-700 rounded text-xs font-semibold"
        >
          Force GC
        </button>
        <button
          onClick={handleClearData}
          className="px-3 py-2 bg-yellow-600 hover:bg-yellow-700 rounded text-xs font-semibold"
        >
          Clear Data
        </button>
        <button
          onClick={() => {
            const analyzer = RefreshMemoryAnalyzer.getInstance();
            const fixer = MemoryLeakFixer.getInstance();
            console.log("Refresh Count:", analyzer.getRefreshCount());
            console.log("Session ID:", analyzer.getSessionId());
            console.log("Snapshots:", analyzer.getSnapshots());
            console.log("Memory Leak Fixer Status:", fixer.getCleanupStatus());
          }}
          className="px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded text-xs font-semibold"
        >
          Log Details
        </button>
        <button
          onClick={() => {
            const fixer = MemoryLeakFixer.getInstance();
            fixer.forceCleanup();
            console.log("🧹 Memory leak fixer cleanup executed");
          }}
          className="px-3 py-2 bg-purple-600 hover:bg-purple-700 rounded text-xs font-semibold"
        >
          Fix Leaks
        </button>
      </div>

      {!isMonitoring && (
        <div className="text-gray-400 text-xs mt-3 text-center">
          Click &quot;Start Monitoring&quot; to begin comprehensive memory leak
          detection
        </div>
      )}

      {isMonitoring && (
        <div className="text-gray-400 text-xs mt-3 text-center">
          Monitoring... Check console for detailed logs
        </div>
      )}
    </div>
  );
}
